import React, { useState } from 'react';
import { CheckCircle, Clock, Loader2, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import {
  useAllPendingArrayApprovals,
  useApproveArrayRequest,
  useIsArrayApprover,
  useRejectArrayRequest,
} from '../../services/arrayCreditReport/queries';

/**
 * F3 — approver inbox for Credit Pull requests.
 *
 * Surfaces every Array credit-pull request currently in `pending_approval`
 * state across all clients, so an approver can triage in one screen instead
 * of clicking into each client's Credit Report tab. Renders nothing if the
 * current user is not an approver.
 *
 * Approve / Reject actions use the same mutations as ArrayCreditPullPanel —
 * single source of truth for the state transition.
 */
const PendingArrayApprovalsPanel: React.FC = () => {
  const isApprover = useIsArrayApprover();
  const { data: pending = [], isPending, isError } = useAllPendingArrayApprovals(isApprover);
  const approveMutation = useApproveArrayRequest();
  const rejectMutation = useRejectArrayRequest();
  const { success, error: toastError } = useToast();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  if (!isApprover) return null;

  const handleApprove = (id: string) => {
    approveMutation.mutate(id, {
      onSuccess: () => success('Approved', 'Request approved.'),
      onError: () => toastError('Approve failed', 'Try again.'),
    });
  };

  const handleReject = () => {
    if (!rejectingId || !rejectReason.trim()) return;
    rejectMutation.mutate(
      { recordId: rejectingId, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          success('Rejected', 'Request rejected.');
          setRejectingId(null);
          setRejectReason('');
        },
        onError: () => toastError('Reject failed', 'Try again.'),
      },
    );
  };

  const fmtDate = (s: string | undefined): string => {
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-yellow-600" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Pending Credit Pull Approvals
          </h3>
          {pending.length > 0 && (
            <span className="rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 text-xs font-semibold text-yellow-900 dark:text-yellow-200">
              {pending.length}
            </span>
          )}
        </div>
      </div>

      <div className="p-4">
        {isPending && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading pending approvals…
          </div>
        )}
        {isError && (
          <div className="text-sm text-red-700 dark:text-red-300">
            Failed to load pending approvals.
          </div>
        )}
        {!isPending && !isError && pending.length === 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <CheckCircle className="h-4 w-4 text-green-600" />
            No requests waiting on you.
          </div>
        )}
        {!isPending && !isError && pending.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="pb-2 font-medium">Client</th>
                <th className="pb-2 font-medium">Requested by</th>
                <th className="pb-2 font-medium">Submitted</th>
                <th className="pb-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((req) => (
                <tr key={req.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-2 text-gray-900 dark:text-gray-100">
                    <div className="font-medium">
                      {req.arrayClientKey?.trim() || req.clientId?.trim() || '(unknown client)'}
                    </div>
                  </td>
                  <td className="py-2 text-gray-700 dark:text-gray-300">
                    {req.requestedBy?.trim() || <span className="text-gray-400 italic">unknown</span>}
                  </td>
                  <td className="py-2 text-gray-500 dark:text-gray-400">
                    {fmtDate(req.requestedAt) || <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleApprove(req.id)}
                        disabled={approveMutation.isPending}
                        className="inline-flex items-center gap-1 rounded bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs disabled:opacity-50"
                      >
                        <ShieldCheck className="h-3 w-3" /> Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejectingId(req.id)}
                        disabled={rejectMutation.isPending}
                        className="inline-flex items-center gap-1 rounded border border-red-600 text-red-700 hover:bg-red-50 px-2 py-1 text-xs"
                      >
                        <ShieldAlert className="h-3 w-3" /> Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reject reason modal */}
      {rejectingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setRejectingId(null)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Reject request — reason
              </h4>
              <button
                type="button"
                onClick={() => setRejectingId(null)}
                aria-label="Cancel rejection"
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (visible to the requester)"
                rows={3}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRejectingId(null)}
                  className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || rejectMutation.isPending}
                  className="rounded bg-red-600 hover:bg-red-700 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {rejectMutation.isPending ? 'Rejecting…' : 'Reject request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingArrayApprovalsPanel;
